<script setup>
/**
 * FormControlModulecomponentsNameField
 * Dynamic drop-down selector filtering fields of type 'text'.
 * Automatically manages field visibility via a clean side-effect watcher
 * if no text attributes are discovered within the target component structure.
 */
import {computed, watch} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useComponentStructureStore} from '@/stores/componentStructure'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
})

const componentsStore = useComponentsStore()
const componentStructureStore = useComponentStructureStore()

// --- Computed ---

/**
 * References global application map indexing valid primary table ids.
 */
const componentIds = computed(() => componentsStore.componentIds)

/**
 * Pulls down the structural fields matching a specific database table name,
 * isolates text fields, and shapes them into a menu list sorted alphabetically.
 */
const options = computed(() => {
    const tableName = props.currentData?.tableName
    const currentId = props.currentData?.id

    if (!currentId || !tableName) {
        return []
    }

    const componentId = componentIds.value[tableName]
    if (!componentId) {
        return []
    }

    // Retrieve fields and filter for text attributes exclusively
    const structureFields = componentStructureStore.get(componentId) || []
    const textFields = structureFields.filter(obj => obj.type === 'text')

    if (!textFields.length) {
        return []
    }

    // Alphabetize text items by field title
    const sortedFields = textFields.sort((a, b) => (a.title > b.title ? 1 : -1))

    // Formulate drop-down configuration collection
    const menuOptions = [{value: 0, text: 'Select…'}]
    sortedFields.forEach((obj) => {
        menuOptions.push({
            value: obj.id,
            text: obj.title
        })
    })

    return menuOptions
})

// --- Watchers ---

/**
 * Monitors the generated options dataset. If it falls empty, it safely triggers
 * a side-effect to hide this field entirely from the active orchestrator form layout.
 * This completely isolates side-effects out of our computational cycle.
 */
watch(options, (newOptions) => {
    if (!newOptions || newOptions.length === 0) {
        if (props.field) {
            // TODO - Update with form-text feature once implemented.
            // It should state that no image columns exist.
            props.field.hidden = true
        }
    }
}, {immediate: true})
</script>

<template>
    <select
        :id="field.id"
        v-model="currentData[field.name]"
        :name="field.name"
        :required="field.required"
        class="form-select"
    >
        <option v-for="option in options" :key="option.value" :value="option.value">
            {{ option.text }}
        </option>
    </select>
</template>